import {
	doesSubmissionStatusMatch,
	hasInviteeSubmittedRfx,
} from "helpers/rfxHelper";
import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import type { ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import { isNullOrUndefined } from "newui/utils/objectUtil";

import { rfxSubmission } from "../Submission";
import { getTabCounts } from "../Submission/components/ViewSubmission/useRfxTabMenu";
import { RFX_SUBMISSION_STATUS } from "../Submission/constants";
import type { RfxSubmission } from "../Submission/types/submission.type";
import type { RfxTabTotals } from "../types/component-types";
import type { Rfx } from "../types/rfx.types";
import type { DisplayRfx } from "../types/rfx.types";
import { isRfxOnboardingType } from "../utils";
import { useRfxData } from "./RfxDataProvider";

interface RfxSubmissionDataContextType {
	currentTab?: string;
	hasRfxSubmissionLoaded: boolean;
	hasSubmitted: boolean;
	isBeingRevised: boolean;
	isClientView: boolean;
	/** PRO-637: viewer is an external evaluation panel member (in neither company). */
	isPanelViewer: boolean;
	isOnboarding: boolean;
	isStatusProgressOrAccepted: boolean;
	rfxData: DisplayRfx | null;
	rfxId: number | null;
	rfxSubmissionData: RfxSubmission;
	rfxSubmissionId: number | null;
	rfxTabTotals: RfxTabTotals;

	// Setters (rarely needed)
	reloadRfxSubmissionData: () => void;
	setRfxTabTotals: (tabTotals: RfxTabTotals) => void;
	updateLocalRfxData: (newRfxData: Rfx | null) => void;
	updateLocalRfxSubmissionData: (
		newRfxSubmissionData: RfxSubmission | null
	) => void;
}

const RfxSubmissionDataContext =
	createContext<RfxSubmissionDataContextType | null>(null);

export function RfxSubmissionDataProvider({
	rfxRouteId,
	rfxInviteRouteId,
	children,
	currentTab,
	useParentRfxData = false,
}: {
	basepath?: string;
	children: ReactNode;
	currentTab: string;
	rfxInviteRouteId: number;
	rfxRouteId: number;
	useParentRfxData: boolean;
}) {
	const parentRfxContext = useRfxData(!useParentRfxData);
	const { currentUser, isGuest } = useCurrentUser();
	const currentCompany = useCurrentCompany();

	const [rfxDataInternal, setRfxDataInternal] = useState<DisplayRfx | null>(
		null
	);

	const rfxData = parentRfxContext?.rfxData ?? rfxDataInternal;
	const [rfxId, setRfxId] = useState<number | null>(
		parentRfxContext?.rfxId ?? (rfxRouteId ? Number(rfxRouteId) : null)
	);

	const [rfxSubmissionData, setRfxSubmissionData] =
		useState<RfxSubmission | null>(null);
	const [rfxSubmissionId, setRfxSubmissionId] = useState<number | null>(
		rfxInviteRouteId ? Number(rfxInviteRouteId) : null
	);
	const [rfxSubmissionStatus, setRfxSubmissionStatus] = useState<string | null>(
		null
	);
	const [hasFailedToLoadId, setHasFailedToLoadId] = useState<number | null>();
	const [isLoadingRfxSubmission, setIsLoadingRfxSubmission] =
		useState<boolean>(false);
	const [loadedRfxSubmission, setLoadedRfxSubmission] = useState<number>(0);
	const [rfxTabTotals, setRfxTabTotals] = useState<RfxTabTotals>({
		addendums: 0,
		clarifications: 0,
		comms: 0,
		documentation: 0,
		evaluation: 0,
		quote: 0,
		returnableSchedule: 0,
	});

	const generateDisplayData = (rfxData: Rfx) => {
		if (!rfxData) {
			return rfxData as DisplayRfx;
		}

		return {
			...rfxData,
			displayName: rfxData.project?.name + " | " + rfxData.supplierList?.name,
		} as DisplayRfx;
	};

	const reloadRfxSubmissionData = () => {
		setLoadedRfxSubmission(0);
		parentRfxContext && parentRfxContext.reloadRfxData();
	};

	const updateLocalRfxData = useCallback(
		(rfx: Rfx) => {
			if (parentRfxContext) {
				parentRfxContext.updateLocalRfxData(rfx);
				return;
			}

			setRfxDataInternal(generateDisplayData(rfx));
			setRfxId(rfx?.id);
		},
		[parentRfxContext]
	);

	useEffect(() => {
		if (!rfxData || !rfxSubmissionData) {
			return;
		}
		setRfxTabTotals(
			getTabCounts(
				rfxSubmissionData?.rfq ?? rfxData,
				rfxSubmissionData?.submission?.rfxAnswers?.length,
				rfxSubmissionData?.submission?.rfxQuoteAnswers?.length,
				rfxSubmissionData.clarifications_count
			)
		);
	}, [rfxData, rfxSubmissionData]);

	const updateLocalRfxSubmissionData = useCallback(
		(rfxSubmission: RfxSubmission) => {
			setRfxSubmissionId(rfxSubmission?.id);
			setLoadedRfxSubmission(rfxSubmission?.id ?? 0);
			setRfxSubmissionData(rfxSubmission);
			setRfxSubmissionStatus(rfxSubmission?.status ?? null);
		},
		[]
	);

	// ────────────────────────────────────────────
	// LOAD FROM API MODE (view/edit/preview)
	// ────────────────────────────────────────────
	useEffect(() => {
		if (
			// !rfxId ||
			(!isGuest && !rfxSubmissionId) ||
			isLoadingRfxSubmission ||
			loadedRfxSubmission === rfxSubmissionId ||
			hasFailedToLoadId === rfxSubmissionId
		) {
			return;
		}

		setIsLoadingRfxSubmission(true);

		const submissionResponse = isGuest
			? rfxSubmission.api.getGuestRfxInvitation(rfxId, currentUser?.id)
			: rfxSubmission.api.getCompanySubmission(rfxSubmissionId);

		submissionResponse
			.then((rfxSubmission) => {
				if (!rfxSubmission) {
					// TODO: need do something nice for user here
					setHasFailedToLoadId(rfxSubmissionId);
					return;
				}

				setHasFailedToLoadId(null);
				!parentRfxContext && updateLocalRfxData(rfxSubmission.rfq);
				updateLocalRfxSubmissionData(rfxSubmission);
			})
			.finally(() => setIsLoadingRfxSubmission(false));
	}, [
		currentUser,
		hasFailedToLoadId,
		isGuest,
		isLoadingRfxSubmission,
		loadedRfxSubmission,
		parentRfxContext,
		rfxId,
		rfxSubmissionId,
		updateLocalRfxData,
		updateLocalRfxSubmissionData,
	]);

	// ────────────────────────────────────────────
	// 3. Derived state
	// ────────────────────────────────────────────
	const isOnboarding = useMemo(() => {
		if (!rfxData) return false;
		return isRfxOnboardingType(rfxData.rfxType || rfxData.type);
	}, [rfxData]);

	const { hasSubmitted, isBeingRevised, isStatusProgressOrAccepted } = useMemo(() => {
		if (isNullOrUndefined(rfxSubmissionStatus)) {
			return { hasSubmitted: false, isStatusProgressOrAccepted: false };
		}

		const hasSubmitted = hasInviteeSubmittedRfx(rfxSubmissionStatus);
		const isBeingRevised = doesSubmissionStatusMatch(
			rfxSubmissionStatus,
			rfxSubmission.constants.RFX_SUBMISSION_STATUS.BEING_REVISED
		);
		const isStatusProgressOrAccepted = doesSubmissionStatusMatch(
			rfxSubmissionStatus,
			[RFX_SUBMISSION_STATUS.ACCEPTED, RFX_SUBMISSION_STATUS.INPROGRESS]
		);
		return { hasSubmitted, isBeingRevised, isStatusProgressOrAccepted };
	}, [rfxSubmissionStatus]);

	const isRfxLoaded = useParentRfxData
		? !isNullOrUndefined(parentRfxContext?.rfxData)
		: rfxId !== 0;

	// Procurer (client) view: user is not a guest AND either owns the RFx or is not the bidder.
	// This is a frontend convenience flag — the API layer enforces access independently via
	// AuthorizesRfxAccess (checks company owns RFx OR has an invite). Draft revisions are
	// filtered server-side in RfxQuoteRevisionController based on company_id comparison.
	/**
	 * PRO-637: an external panel member belongs to NEITHER the procuring company
	 * nor the responding supplier — they were invited onto the panel from outside
	 * both. They are evaluating the submission, so they need the procurer-side
	 * view; without this they fell through to the bidder branch and were shown
	 * "Submission deadline has passed, your submission was made successfully" —
	 * a message about a submission that is not theirs.
	 *
	 * Inferring from "in neither company" is safe precisely because this is a
	 * presentation flag and the API is the real gate: the only way to load this
	 * page while in neither company is RfqPolicy's `activePanelMembership` grant.
	 * All ids must be present, or an unloaded/guest state (`undefined`) would
	 * compare unequal to everything and spuriously read as a panel viewer.
	 */
	const isPanelViewer = Boolean(
		currentCompany?.id &&
		rfxData?.companyId &&
		rfxSubmissionData?.company_id &&
		rfxData.companyId !== currentCompany.id &&
		rfxSubmissionData.company_id !== currentCompany.id
	);

	// Procurer (client) view: user is not a guest AND either owns the RFx or is not the bidder.
	// This is a frontend convenience flag — the API layer enforces access independently via
	// AuthorizesRfxAccess (checks company owns RFx OR has an invite). Draft revisions are
	// filtered server-side in RfxQuoteRevisionController based on company_id comparison.
	const isClientView = isPanelViewer || (!isGuest &&
		(rfxData?.companyId === currentCompany?.id &&
			rfxSubmissionData?.company_id !== currentCompany?.id));

	return (
		<RfxSubmissionDataContext.Provider
			value={{
				currentTab,
				hasRfxSubmissionLoaded: loadedRfxSubmission !== 0 && isRfxLoaded,
				hasSubmitted,
				isBeingRevised,
				isClientView,
				isPanelViewer,
				isOnboarding,
				isStatusProgressOrAccepted,
				rfxData,
				rfxId,
				rfxSubmissionData,
				rfxSubmissionId,
				rfxTabTotals,
				reloadRfxSubmissionData,
				setRfxTabTotals,
				updateLocalRfxData,
				updateLocalRfxSubmissionData,
			}}
		>
			{children}
		</RfxSubmissionDataContext.Provider>
	);
}

export function useRfxSubmissionData() {
	const context = useContext(RfxSubmissionDataContext);
	if (!context) {
		throw new Error(
			"useRfxSubmissionData must be used inside <RfxSubmissionDataContext>"
		);
	}
	return context;
}
